// src/components/tuition/TuitionStudentPortal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaUserGraduate,
  FaBookOpen,
  FaCalendarCheck,
  FaAward,
  FaVideo,
  FaMoneyBillWave,
  FaPlay,
  FaCheckCircle,
  FaClock,
  FaExternalLinkAlt,
  FaCopy,
  FaArrowRight,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useAppTheme } from "../../context/AppThemeContext";
import { useTenant } from "../../context/TenantContext";
import api from "../../api/httpClient";
import { API_PATHS } from "../../api/apiPaths";
import { TUITION_MODULE_KEYS } from "./tuitionMockData";
import { normalizeVisibleOnlineClasses } from "./tuitionOnlineClass";

const unwrapList = (response, keys = []) => {
  const payload = response?.data?.data ?? response?.data;
  if (Array.isArray(payload)) return payload;
  for (const key of keys) if (Array.isArray(payload?.[key])) return payload[key];
  return [];
};

export default function TuitionStudentPortal() {
  const { isDarkMode } = useAppTheme();
  const { activeTenant, tenantFeatures } = useTenant();
  const currentInstitute = activeTenant;
  const enabledModules = tenantFeatures?.enabledModules || currentInstitute?.enabledModules || {};
  const studentRecordsEnabled = enabledModules[TUITION_MODULE_KEYS.STUDENT_MANAGEMENT] === true
    || enabledModules[TUITION_MODULE_KEYS.ATTENDANCE] === true
    || enabledModules[TUITION_MODULE_KEYS.ASSESSMENTS] === true
    || enabledModules[TUITION_MODULE_KEYS.FEES_FINANCIALS] === true;
  const onlineClassesEnabled = enabledModules[TUITION_MODULE_KEYS.ONLINE_CLASSES] === true;
  const [allStudents, setAllStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Current authenticated student record
  const currentStudent = useMemo(() => {
    return allStudents[0];
  }, [allStudents]);

  const [onlineClasses, setOnlineClasses] = useState([]);
  const [activeTab, setActiveTab] = useState("classes"); // "classes" | "marks" | "fees" | "attendance"

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");
    Promise.all([
      studentRecordsEnabled ? api.get(API_PATHS.tuition.students()) : Promise.resolve(null),
      onlineClassesEnabled ? api.get(API_PATHS.tuition.onlineClasses()) : Promise.resolve(null),
    ]).then(([studentResponse, classResponse]) => {
      if (!active) return;
      setAllStudents(unwrapList(studentResponse, ["students", "items"]));
      setOnlineClasses(normalizeVisibleOnlineClasses(unwrapList(classResponse, ["rooms", "classes", "items"])));
    }).catch((error) => {
      if (active) setLoadError(error?.message || "Unable to load your tuition portal.");
    }).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [studentRecordsEnabled, onlineClassesEnabled]);

  const liveClasses = useMemo(() => {
    return (onlineClasses || []).filter((c) => c && c.status === "live");
  }, [onlineClasses]);

  const upcomingClasses = useMemo(() => {
    return (onlineClasses || []).filter((c) => c && c.status !== "ended");
  }, [onlineClasses]);
  const averagePercentage = useMemo(() => {
    const marks = Array.isArray(currentStudent?.recentMarks) ? currentStudent.recentMarks : [];
    if (!marks.length) return null;
    const percentages = marks
      .filter((item) => Number(item.maxMarks) > 0)
      .map((item) => (Number(item.marks || 0) / Number(item.maxMarks)) * 100);
    if (!percentages.length) return null;
    return Math.round(percentages.reduce((sum, value) => sum + value, 0) / percentages.length);
  }, [currentStudent]);

  if (loading) return <div className="p-8 text-center" role="status">Loading tuition details…</div>;

  return (
    <div className={`min-h-screen pb-16 pt-4 px-3 sm:px-6 lg:px-8 ${isDarkMode ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`}>
      <div className="mx-auto max-w-6xl space-y-6">
        {loadError && <div role="alert" className="rounded-2xl border border-rose-300 bg-rose-50 p-4 text-sm font-semibold text-rose-700">{loadError}</div>}

        {/* Student Profile Banner */}
        <div className={`rounded-3xl border p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-tr from-sky-600 to-emerald-600 text-white flex items-center justify-center text-2xl font-bold shadow-md shadow-sky-600/20">
              <FaUserGraduate />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-sky-100 dark:bg-sky-950/60 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-sky-700 dark:text-sky-300">
                  Student Portal
                </span>
                <span className="text-xs text-slate-400 font-semibold">
                  {currentInstitute?.name}
                </span>
              </div>
              <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-slate-100 mt-1">
                {currentStudent?.name}
              </h1>
              <p className="text-xs text-slate-500">
                Roll No: <span className="font-mono font-bold text-sky-500">{currentStudent?.rollNo}</span> • Batch: {currentStudent?.batch}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-2xl border border-sky-200 px-4 py-2.5 text-xs font-bold text-sky-700 dark:border-sky-900 dark:text-sky-300"><FaVideo /> Select a scheduled class below</span>
          </div>
        </div>

        {/* KPI Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className={`rounded-3xl border p-5 shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Attendance Rate</span>
            <div className="mt-2 text-2xl font-black text-emerald-500">
              {Number(currentStudent?.attendanceRate || 0)}%
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {currentStudent ? "Based on recorded attendance" : "No attendance data"}
            </p>
          </div>

          <div className={`rounded-3xl border p-5 shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Average Grade</span>
            <div className="mt-2 text-2xl font-black text-sky-500">
              {averagePercentage === null ? "—" : `${averagePercentage}%`}
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">Based on recorded assessments</p>
          </div>

          <div className={`rounded-3xl border p-5 shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Pending Tuition Fee</span>
            <div className="mt-2 text-2xl font-black text-rose-500">
              ₹{(currentStudent?.remainingFee || 0).toLocaleString("en-IN")}
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">Due: {currentStudent?.dueDate || "Not set"}</p>
          </div>

          <div className={`rounded-3xl border p-5 shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Enrolled Subjects</span>
            <div className="mt-2 text-xl font-bold truncate">
              {Array.isArray(currentStudent?.subjects) ? currentStudent.subjects.join(", ") : currentStudent?.subjects || "PCM"}
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">Regular Batch</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
          <button
            type="button"
            onClick={() => setActiveTab("classes")}
            className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${activeTab === "classes" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
          >
            <FaVideo /> Live & Scheduled Classes ({upcomingClasses.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("marks")}
            className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${activeTab === "marks" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
          >
            <FaAward /> Test Results & Grades
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("fees")}
            className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition ${activeTab === "fees" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
          >
            <FaMoneyBillWave /> Tuition Fees & Receipts
          </button>
        </div>

        {/* -------------------------------------------------------------
            TAB 1: LIVE & SCHEDULED CLASSES
        ------------------------------------------------------------- */}
        {activeTab === "classes" && (
          <div className="space-y-6">
            {/* Live Now Highlight Banner */}
            {liveClasses.length > 0 && (
              <div className="rounded-3xl border border-emerald-500/40 bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-sky-500/10 p-6 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1 text-xs font-black uppercase tracking-wider text-white shadow-sm animate-pulse">
                      ● Live Classroom Right Now
                    </span>
                    <h3 className="mt-2 text-lg sm:text-xl font-black text-slate-900 dark:text-slate-100">
                      {liveClasses[0].topic}
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Faculty: {liveClasses[0].teacherName} • Subject: {liveClasses[0].subject} • Meeting ID:{" "}
                      <span className="font-mono font-bold text-sky-500">{liveClasses[0].meetingId}</span>
                    </p>
                  </div>

                  <Link
                    to={`/classroom/join/${liveClasses[0].meetingId}`}
                    state={{ scheduledClass: liveClasses[0], student: currentStudent }}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-6 py-3 font-bold text-white shadow-lg shadow-emerald-600/30 hover:bg-emerald-700 transition"
                  >
                    <FaPlay /> Join Live Lecture Now
                  </Link>
                </div>
              </div>
            )}

            {/* Scheduled Lectures Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcomingClasses.map((cls) => (
                <div
                  key={cls.id}
                  className={`rounded-3xl border p-5 shadow-sm flex flex-col justify-between ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}
                >
                  <div>
                    <div className="flex items-center justify-between">
                      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase ${cls.status === "live" ? "bg-emerald-500/20 text-emerald-500" : "bg-sky-500/20 text-sky-500"}`}>
                        {cls.status === "live" ? "● LIVE" : "SCHEDULED"}
                      </span>
                      <span className="text-xs font-bold text-slate-400">{cls.subject}</span>
                    </div>

                    <h4 className="font-bold text-sm mt-2 text-slate-900 dark:text-slate-100">{cls.topic}</h4>
                    <p className="text-xs text-slate-500 mt-0.5">Faculty: {cls.teacherName}</p>

                    <div className="mt-3 flex items-center justify-between p-2.5 rounded-xl bg-slate-100 dark:bg-slate-950/60 text-xs">
                      <div>
                        <span className="text-[10px] font-bold text-slate-400 uppercase">Meeting ID:</span>
                        <p className="font-mono font-bold text-sky-500">{cls.meetingId || "Not assigned"}</p>
                      </div>
                      {cls.meetingId && <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard?.writeText(cls.meetingId);
                          toast.success("Meeting code copied!");
                        }}
                        className="p-1.5 text-slate-400 hover:text-slate-600"
                      >
                        <FaCopy />
                      </button>}
                    </div>

                    <p className="mt-2 text-[11px] text-slate-400 flex items-center gap-1.5">
                      <FaClock /> {cls.scheduledDate} from {cls.startTime} to {cls.endTime}
                    </p>
                  </div>

                  <div className="mt-5 pt-3 border-t border-slate-100 dark:border-slate-800">
                    {cls.meetingId ? (
                      <Link to={`/classroom/join/${encodeURIComponent(cls.meetingId)}`} state={{ scheduledClass: cls, student: currentStudent }} className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-sky-600 py-2 text-xs font-bold text-white hover:bg-sky-700 transition">
                        <FaPlay /> Enter / Join Class
                      </Link>
                    ) : <p role="status" className="rounded-xl bg-amber-50 p-2 text-center text-xs font-semibold text-amber-700">Room information is not available yet.</p>}
                  </div>
                </div>
              ))}
            </div>
            {!upcomingClasses.length && !loadError && <div className="rounded-2xl border border-slate-200 p-6 text-center text-sm text-slate-500 dark:border-slate-800">No scheduled classes are currently available.</div>}
          </div>
        )}

        {/* -------------------------------------------------------------
            TAB 2: TEST RESULTS & REPORT CARD
        ------------------------------------------------------------- */}
        {activeTab === "marks" && (
          <div className="space-y-4">
            <div className={`overflow-x-auto rounded-3xl border shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
              <div className="p-4 border-b border-slate-200 dark:border-slate-800">
                <h3 className="font-bold text-sm">Unit Test & Assessment Performance</h3>
                <p className="text-xs text-slate-400">Continuous evaluation marks and teacher grades</p>
              </div>

              <table className="w-full text-left text-xs">
                <thead className={`border-b text-[11px] font-bold uppercase tracking-wider text-slate-400 ${isDarkMode ? "border-slate-800 bg-slate-950/40" : "border-slate-200 bg-slate-50"}`}>
                  <tr>
                    <th className="px-4 py-3">Assessment Name</th>
                    <th className="px-4 py-3">Marks Scored</th>
                    <th className="px-4 py-3">Percentage</th>
                    <th className="px-4 py-3">Letter Grade</th>
                    <th className="px-4 py-3 text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {currentStudent?.recentMarks?.map((mark, i) => (
                    <tr key={i} className="hover:bg-slate-500/5 transition">
                      <td className="px-4 py-3 font-bold text-slate-900 dark:text-slate-100">{mark.testName}</td>
                      <td className="px-4 py-3 font-semibold">{mark.marks} / {mark.maxMarks}</td>
                      <td className="px-4 py-3 font-bold text-sky-500">
                        {Math.round((mark.marks / mark.maxMarks) * 100)}%
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded-md bg-emerald-500/10 px-2 py-0.5 text-xs font-bold text-emerald-600 dark:text-emerald-400">
                          {mark.grade}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="inline-flex items-center gap-1 font-bold text-emerald-500 text-[11px]">
                          <FaCheckCircle /> Evaluated
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* -------------------------------------------------------------
            TAB 3: TUITION FEES
        ------------------------------------------------------------- */}
        {activeTab === "fees" && (
          <div className="space-y-4">
            <div className={`rounded-3xl border p-6 shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
              <h3 className="font-bold text-base mb-1">Tuition Fee Ledger</h3>
              <p className="text-xs text-slate-400 mb-6">Payment history and remaining balance</p>

              <div className="grid sm:grid-cols-3 gap-4 mb-6">
                <div className="rounded-2xl p-4 bg-slate-100 dark:bg-slate-950/60">
                  <span className="text-[10px] font-bold text-slate-400 uppercase">Total Agreed Fee</span>
                  <p className="text-xl font-black mt-1">₹{(currentStudent?.totalFee || 0).toLocaleString("en-IN")}</p>
                </div>
                <div className="rounded-2xl p-4 bg-emerald-500/10">
                  <span className="text-[10px] font-bold text-emerald-500 uppercase">Paid Amount</span>
                  <p className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-1">
                    ₹{(currentStudent?.paidFee || 0).toLocaleString("en-IN")}
                  </p>
                </div>
                <div className="rounded-2xl p-4 bg-rose-500/10">
                  <span className="text-[10px] font-bold text-rose-500 uppercase">Pending Due</span>
                  <p className="text-xl font-black text-rose-600 dark:text-rose-400 mt-1">
                    ₹{(currentStudent?.remainingFee || 0).toLocaleString("en-IN")}
                  </p>
                </div>
              </div>

              {currentStudent?.remainingFee > 0 && (
                <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <p className="font-bold text-amber-700 dark:text-amber-400">
                      Payment Reminder: ₹{currentStudent.remainingFee.toLocaleString("en-IN")} is due by {currentStudent.dueDate || "this month"}
                    </p>
                    <p className="text-slate-500 mt-0.5">Please ask your parent to clear fees to maintain uninterrupted live lecture access.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => toast.info("Online UPI payment portal opens via ClassNX Gateway.")}
                    className="rounded-xl bg-amber-600 px-4 py-2 font-bold text-white shadow-sm hover:bg-amber-700 transition"
                  >
                    Pay via UPI / Card
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
